import { ADMIN_EMAIL } from 'Config';
import AppData from 'Data/AppData';
import { getUserOrgIds } from 'Data/context/Organizations';
import { user_org_roles, user_roles } from 'Data/Enum';
import {
  getRequestMessagesUpdatedHTML,
  getRequestRequiresApprovalHTML,
} from 'Data/lib/emailTemplateHelper';
import { LineItemData } from 'Data/LineItemData';
import OrgData from 'Data/OrgData';
import RequestApprovalRequestsData from 'Data/RequestApprovalRequestsData';
import { post } from 'Data/SystemREST';
import { REQUEST_STATUS, REQUEST_STATUS_KEYS } from 'Pages/Requests/Enum';
import { canUserViewAllCores, isUserAuthorized } from 'Util';
import { getDateRangeForYear } from 'Util/dateUtil';
import GenericTableData from './GenericTableData';

const requestListIncludes =
  '$line_items.shipping,transactions,request_approval_requests';

const getDateRangeQueryParam = (fiscalYear) => {
  return [...getDateRangeForYear(fiscalYear), '$NULL$'];
};

// Request Module Event Bus. Handles all interactions with Requests.
let RequestData = GenericTableData.getTableDataBus('REQ', 'requests');

RequestData.getMyRequestsFunction = (fiscalYear) => {
  return (opts) => {
    let where = {
      ...(opts?.where && { ...opts.where }),
    };
    if (fiscalYear?.length) {
      where['*date_requested'] = getDateRangeQueryParam(fiscalYear);
    }
    where['*requesting_user_id'] = AppData.userID();

    let options = {
      ...opts,
      where: where,
      _include: requestListIncludes,
    };
    return RequestData.getRecordList(options, true);
  };
};

RequestData.getRequestListFunction = (fiscalYear) => {
  return (opts) => {
    let where = {
      ...(opts?.where && { ...opts.where }),
    };
    if (
      !isUserAuthorized({
        roles: [user_roles.ADMINISTRATOR],
      }) &&
      !Object.hasOwn(where, '*parent_organization_id')
    ) {
      where['*parent_organization_id'] = getUserOrgIds();
    }
    if (fiscalYear?.length) {
      where['*date_requested'] = getDateRangeQueryParam(fiscalYear);
    }

    let options = {
      ...opts,
      where: where,
      _include: requestListIncludes,
    };

    return RequestData.getRecordList(options, true);
  };
};

RequestData.getRequestedOrdersListFunction = (fiscalYear) => {
  return (opts) => {
    let where = {
      ...(opts?.where && { ...opts.where }),
      '*processing_user_id': '$NULL$',
      '*status': ['1_SUBMITTED', '2_APPROVED', '3_TRANSACTION_CREATED'],
      ...(fiscalYear?.length && {
        '*date_requested': getDateRangeQueryParam(fiscalYear),
      }),
    };
    if (
      !isUserAuthorized({
        roles: [user_roles.ADMINISTRATOR],
      }) &&
      !Object.hasOwn(where, '*parent_organization_id')
    ) {
      where['*parent_organization_id'] = getUserOrgIds();
    }

    let options = {
      ...opts,
      where: where,
      _include: requestListIncludes,
    };
    return RequestData.getRecordList(options, true);
  };
};

let exportCols = [
  { key: 'id', header: 'ID' },
  {
    key: 'status',
    header: 'Status',
    labelMap: Object.values(REQUEST_STATUS).reduce(
      (mapObject, { key, label }) => {
        mapObject[key] = label;
        return mapObject;
      },
      {},
    ),
  },
  { key: 'date_requested', header: 'Date Requested' },
  { key: 'date_approved', header: 'Date Approved' },
  { key: 'organization', header: 'Core Facility' },
  { key: 'requester', header: 'Requester' },
  { key: 'processor', header: 'Processor' },
  { key: 'vendor', header: 'Vendor' },
  { key: 'notes', header: 'Description' },
  { key: 'total_amount', header: 'Amount' },
  { key: 'associatedResources', header: 'Resources' },
];

RequestData.getExportRequestsFunc = (fiscalYear) => {
  return (opts) => {
    let where = {
      ...(opts?.where && { ...opts.where }),
    };
    if (
      !canUserViewAllCores() &&
      !Object.hasOwn(where, '*parent_organization_id')
    ) {
      where['*parent_organization_id'] = getUserOrgIds();
    }
    if (fiscalYear?.length) {
      where['*date_requested'] = getDateRangeQueryParam(fiscalYear);
    }

    let columns = exportCols;

    let options = {
      ...opts,
      where: where,
      _include: 'transactions,line_items',
      columns,
    };
    return RequestData.exportRecordList(options);
  };
};

RequestData.getExportMyRequestsFunc = (fiscalYear) => {
  return (opts) => {
    let where = {
      ...(opts?.where && { ...opts.where }),
    };
    if (fiscalYear?.length) {
      where['*date_requested'] = getDateRangeQueryParam(fiscalYear);
    }
    where['*requesting_user_id'] = AppData.userID();

    let columns = exportCols;

    let options = {
      ...opts,
      where: where,
      columns,
    };
    return RequestData.exportRecordList(options);
  };
};

RequestData.getExportOrdersFunc = (fiscalYear) => {
  return (opts) => {
    let where = {
      ...(opts?.where && { ...opts.where }),
      '*processing_user_id': '$NULL$',
      '*status': ['1_SUBMITTED', '2_APPROVED', '3_TRANSACTION_CREATED'],
      ...(fiscalYear?.length && {
        '*date_requested': getDateRangeQueryParam(fiscalYear),
      }),
    };

    if (
      !canUserViewAllCores() &&
      !Object.hasOwn(where, '*parent_organization_id')
    ) {
      where['*parent_organization_id'] = getUserOrgIds();
    }

    let columns = exportCols;

    let options = {
      ...opts,
      where: where,
      columns,
    };
    return RequestData.exportRecordList(options);
  };
};

RequestData.getRequest = function (id) {
  return RequestData.getRecord(
    id,
    {
      _include:
        'request_notes,line_items,$transactions.documents,request_approval_requests,documents,$line_items.shipping',
    },
    true,
    'REQUEST_LOADED',
  );
};

RequestData.getBlankRequest = function () {
  return {
    id: null,
    status: REQUEST_STATUS_KEYS.NEW,
    shipping_speed: '0_STANDARD',
    requested_delivery_address_id: null,
    notes: '',
    ...(!isUserAuthorized({
      roles: [
        user_roles.ADMINISTRATOR,
        user_roles.ADMINISTRATIVE_APPROVER,
        user_roles.BUYER,
        user_roles.TRANSACTOR,
        ['*', user_org_roles.MANAGER],
      ],
    }) && { requesting_user_id: AppData.userID() }),
    ...(isUserAuthorized({
      roles: [user_roles.BUYER, user_roles.TRANSACTOR],
    }) && { processing_user_id: AppData.userID() }),
    ...(!isUserAuthorized({
      roles: [
        user_roles.ADMINISTRATOR,
        user_roles.ADMINISTRATIVE_APPROVER,
        user_roles.BUYER,
        user_roles.TRANSACTOR,
      ],
    }) &&
      getUserOrgIds()?.length === 1 && {
        parent_organization_id: getUserOrgIds()[0],
      }),
  };
};

RequestData.moveRequestStatusForward = function (request) {
  if (!request.id || !request.status) {
    return;
  }

  let statusObject = REQUEST_STATUS[request.status];
  if (!statusObject) {
    return;
  }

  return RequestData.updateStatus(request, statusObject.next_step_key);
};

const getRequestDetails = (
  {
    notes,
    parent_organization_id,
    requested_delivery_address_id,
    requesting_user_id,
    shipping_speed,
    vendor_id,
    line_items,
  },
  includeLines = false,
) => {
  return {
    notes,
    parent_organization_id,
    requested_delivery_address_id,
    ...(!isUserAuthorized({
      roles: [
        user_roles.ADMINISTRATOR,
        user_roles.ADMINISTRATIVE_APPROVER,
        user_roles.BUYER,
        user_roles.TRANSACTOR,
        ['*', user_org_roles.MANAGER],
      ],
    })
      ? { requesting_user_id: AppData.userID() } // Default the requester to the current user
      : { requesting_user_id }), // If admin or manager, the requester remains and the user can edit it
    shipping_speed,
    vendor_id,
    ...(includeLines && {
      line_items: line_items.map(LineItemData.getSimpleLineItem),
    }),
    ...(line_items?.length && {
      shipping_amount: line_items[0].shipping?.amount || 0,
    }),
    ...(isUserAuthorized({
      roles: [user_roles.BUYER, user_roles.TRANSACTOR],
    }) && { processing_user_id: AppData.userID() }),
  };
};

RequestData.getRequestDetails = getRequestDetails;

RequestData.createRequest = (requestObject) => {
  let lines =
    requestObject.line_items?.length > 0
      ? requestObject.line_items.map((line) =>
          LineItemData.getSimpleLineItem(line),
        )
      : [];

  return RequestData.createRecord(
    {
      ...requestObject,
      status: REQUEST_STATUS_KEYS.NEW,
      ...(lines?.length && {
        line_items: lines,
        _include: 'line_items',
      }),
    },
    'REQUEST_CREATED',
  );
};

const getRARForRequest = (request) => {
  let rar;
  if (request.request_approval_requests?.length) {
    let rars = request.request_approval_requests.filter(
      (rar) => rar.approver_id === AppData.userID(),
    );
    rar = rars?.length && rars[0];
  }

  return rar;
};
const getRARsWithDates = (request) => {
  let rars = [];
  if (request.request_approval_requests?.length) {
    rars = request.request_approval_requests.filter(
      (rar) => rar.date_approved || rar.date_rejected,
    );
  }

  return rars;
};

RequestData.updateStatus = async function (
  request,
  newStatusKey,
  busListenerActionName = 'REQUEST_STATUS_UPDATED',
) {
  if (!request.id || !newStatusKey) {
    return;
  }

  let createRequestApprovalRequests = false;
  let requiresApproval = false;
  let requestApproverIds = [];
  let busActionName = busListenerActionName;

  let updatedRequestInfo = {
    id: request.id,
    status: newStatusKey,
    parent_organization_id: request.parent_organization_id,
  };

  try {
    let organization = await OrgData.getRecord(request.parent_organization_id, {
      _include: 'user_organization_roles',
    });

    requiresApproval =
      [
        REQUEST_STATUS_KEYS.NEW,
        REQUEST_STATUS_KEYS.SUBMITTED,
        REQUEST_STATUS_KEYS.APPROVED,
      ].includes(newStatusKey) &&
      ((request.request_approval_requests &&
        request.request_approval_requests?.length > 0) ||
        organization?.roles?.some((uor) => uor.role === 'REQUEST_APPROVER') ||
        false);
    requestApproverIds =
      organization?.roles
        ?.filter((uor) => uor.role === 'REQUEST_APPROVER')
        .map((uor) => uor.user_id) || [];

    if (requiresApproval && !request.request_approval_requests?.length) {
      createRequestApprovalRequests = true;
    }
  } catch (e) {
    console.log(
      `Not able to get approval requirements for org: ${request.parent_organization_id}. Error: ${e}`,
    );
  }

  if (newStatusKey === REQUEST_STATUS_KEYS.SUBMITTED) {
    if (!request.date_requested) {
      updatedRequestInfo.date_requested = new Date();
    }
    updatedRequestInfo.date_approved = null;
    updatedRequestInfo.date_rejected = null;

    if (!requiresApproval) {
      updatedRequestInfo.date_approved = new Date();
      updatedRequestInfo.status = REQUEST_STATUS_KEYS.APPROVED;
      busActionName = 'APPROVAL_SKIPPED';
    }
  }

  if (newStatusKey === REQUEST_STATUS_KEYS.APPROVED) {
    updatedRequestInfo.date_approved = new Date();
  }
  if (newStatusKey === REQUEST_STATUS_KEYS.REJECTED) {
    updatedRequestInfo.date_rejected = new Date();
  }
  if (newStatusKey === REQUEST_STATUS_KEYS.PURCHASED) {
    updatedRequestInfo.date_processed = new Date();
  }

  RequestData.updateRecord(updatedRequestInfo, busActionName).then(
    (updatedRequest) => {
      let newStatusKey = updatedRequest.status;

      if (createRequestApprovalRequests) {
        if (newStatusKey === REQUEST_STATUS_KEYS.APPROVED) {
          RequestApprovalRequestsData.updateOrCreateRecords([
            {
              parent_request_id: request.id,
              approver_id: AppData.userID(),
              date_approved: new Date(),
            },
          ]);
        } else if (newStatusKey === REQUEST_STATUS_KEYS.REJECTED) {
          RequestApprovalRequestsData.updateOrCreateRecords([
            {
              parent_request_id: request.id,
              approver_id: AppData.userID(),
              date_rejected: new Date(),
            },
          ]);
        } else {
          RequestApprovalRequestsData.updateOrCreateRecords(
            requestApproverIds.map((approverId) => {
              return {
                parent_request_id: request.id,
                approver_id: approverId,
              };
            }),
          );
        }
      } else {
        if (requiresApproval) {
          if (newStatusKey === REQUEST_STATUS_KEYS.APPROVED) {
            let rar = getRARForRequest(request);

            if (rar) {
              RequestApprovalRequestsData.updateRecord({
                id: rar.id,
                date_approved: new Date(),
                date_rejected: null,
              });
            } else {
              RequestApprovalRequestsData.createRecord({
                parent_request_id: request.id,
                date_approved: new Date(),
                approver_id: AppData.userID(),
              });
            }
          } else if (newStatusKey === REQUEST_STATUS_KEYS.REJECTED) {
            let rar = getRARForRequest(request);
            if (rar) {
              RequestApprovalRequestsData.updateRecord({
                id: rar.id,
                date_rejected: new Date(),
                date_approved: null,
              });
            } else {
              RequestApprovalRequestsData.createRecord({
                parent_request_id: request.id,
                date_rejected: new Date(),
                approver_id: AppData.userID(),
              });
            }
          } else if (request.request_approval_requests?.length) {
            let datedRARs = getRARsWithDates(request);
            if (datedRARs.length > 0) {
              RequestApprovalRequestsData.updateOrCreateRecords(
                datedRARs.map((rar) => {
                  return {
                    ...rar,
                    date_approved: null,
                    date_rejected: null,
                  };
                }),
              );
            }
          }
        }
      }
    },
  );
};

RequestData.updateShippingSpeed = (request, shipping_speed) => {
  if (
    request?.id &&
    shipping_speed &&
    request.shipping_speed !== shipping_speed
  ) {
    let updatedRequest = {
      id: request.id,
      shipping_speed: shipping_speed,
      parent_organization_id: request.parent_organization_id,
    };

    return RequestData.updateRecord(updatedRequest, 'REQUEST_SHIPPING_UPDATED');
  }
};

RequestData.claim = (request) => {
  RequestData.getRequest(request.id).then((returnedRequest) => {
    if (returnedRequest.processing_user_id) {
      RequestData.notifyListeners('REQUEST_ALREADY_CLAIMED', false);
      return false;
    }
    if (returnedRequest.status > REQUEST_STATUS_KEYS.SUBMITTED) {
      let updatedRequest = {
        id: request.id,
        processing_user_id: AppData.userID(),
        parent_organization_id: request.parent_organization_id,
      };
      return RequestData.updateRecord(updatedRequest, 'REQUEST_CLAIMED');
    } else {
      RequestData.notifyListeners('REQUEST_NOT_YET_SUBMITTED', false);
      return false;
    }
  });
};

RequestData.sendUpdateEmail = (request, updateInfo) => {
  if (!request) return;

  let html = getRequestMessagesUpdatedHTML(
    request.id,
    updateInfo.action,
    updateInfo.note,
  );

  let subscribers = [request.requester, request.processor]
    .filter((user) => user && user.id !== AppData.userID())
    .map((user) => user.emails);
  if (!subscribers.length) return;

  let emailData = {
    from: `HSC BOS Admin Team<${ADMIN_EMAIL}>`,
    to: subscribers.join(','),
    subject: `Update to BOS Request ${request.id}`,
    html,
  };

  let requestURL = `/emailer`;
  return post(requestURL)
    .send({ emailData })
    .then((res) => {
      return Promise.resolve(res);
    })
    .catch((err) => {
      console.log(err);
      RequestData.notifyListeners('REQUEST_EMAIL_ERROR', {
        message: `Unable to email request updates`,
        debug: err,
      });
    });
};

RequestData.sendApprovalRequestEmail = (request, approver) => {
  if (!request || !approver) return;

  if (approver.id === AppData.userID()) return;

  let html = getRequestRequiresApprovalHTML(request, approver);

  let emailData = {
    from: `HSC BOS Admin Team<${ADMIN_EMAIL}>`,
    to: approver.emails,
    subject: `BOS Request Requiring Approval`,
    html,
  };

  let requestURL = `/emailer`;
  return post(requestURL)
    .send({ emailData })
    .then((res) => {
      return Promise.resolve(res);
    })
    .catch((err) => {
      console.log('caught error in request data');
      console.log(err);
      RequestData.notifyListeners('REQUEST_APPROVAL_EMAIL_ERROR', {
        message: `Unable to email request approval requests`,
        debug: err,
      });
    });
};

export default RequestData;
